import { useEffect, useMemo } from 'react';
import { Transition } from '@headlessui/react';
import { MailIcon } from '@heroicons/react/outline';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import {
  MailSequenceEnrollmentStatus,
  MailSequenceScheduledSendStatus,
  useMailSequenceEnrollmentsForProgramQuery,
  useProgramFollowUpsQuery,
} from '../../../../generated/graphql';

import { defaultTransitionProps } from '../../../lib/animation';

import IllustrationMail from '../../../svgs/IllustrationMail';
import Spinner from '../../../svgs/Spinner';

import Button from '../../../components/Button';

import { usePatientProfileContext } from '..';
import EmptyProfileTab from '../../../components/EmptyProfileTab';
import { customToast } from '../../../components/ToastAlert/customToast';
import FollowUpSelector from '../../../components/FollowUpSelector';
import { sequenceDisplayTitle } from '../../../lib/mailSequence';
import { UpcomingScheduledMail } from './types';

const VISIBLE_SCHEDULED_STATUSES = new Set<MailSequenceScheduledSendStatus>([
  MailSequenceScheduledSendStatus.Pending,
  MailSequenceScheduledSendStatus.Sending,
  MailSequenceScheduledSendStatus.Failed,
]);

const FollowUps = () => {
  const navigate = useNavigate();
  const params = useParams();
  const programId = params.programId!;
  const programFollowUpId = params.programFollowUpId;
  const scheduledSendId = params.scheduledSendId;

  const { patient } = usePatientProfileContext();

  const {
    data: programFollowUpsData,
    error: programFollowUpsError,
    loading: programFollowUpsLoading,
    refetch: refetchProgramFollowUps,
  } = useProgramFollowUpsQuery({
    variables: {
      programId,
    },
    fetchPolicy: 'network-only',
    onError: (error) => {
      console.error('Error fetching follow-ups:', error);
      customToast.error('Unable to fetch your mail.');
    },
  });

  const {
    data: enrollmentsData,
    loading: enrollmentsLoading,
    error: enrollmentsError,
    refetch: refetchEnrollments,
  } = useMailSequenceEnrollmentsForProgramQuery({
    variables: { programId },
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      console.error('Error fetching mail sequences:', error);
    },
  });

  const programFollowUps = programFollowUpsData?.programFollowUps;

  const upcomingScheduledMails = useMemo((): UpcomingScheduledMail[] => {
    const enrollments =
      enrollmentsData?.mailSequenceEnrollmentsForProgram ?? [];
    const items: UpcomingScheduledMail[] = [];

    for (const enrollment of enrollments) {
      if (enrollment.status === MailSequenceEnrollmentStatus.Cancelled) {
        continue;
      }

      for (const send of enrollment.scheduledSends) {
        if (send.status === MailSequenceScheduledSendStatus.Cancelled) {
          continue;
        }
        if (!VISIBLE_SCHEDULED_STATUSES.has(send.status)) {
          continue;
        }
        // Already-sent mail appears in the sent follow-ups list.
        if (send.programFollowUpId) {
          continue;
        }

        items.push({
          id: send.id,
          enrollmentId: enrollment.id,
          stepIndex: send.stepIndex,
          sequenceTitle: sequenceDisplayTitle(enrollment),
          subject: send.subject,
          providerNote: send.providerNote,
          scheduledDate: send.scheduledDate,
          defaultSendTime: enrollment.defaultSendTime,
          status: send.status,
          failureReason: send.failureReason,
          programFollowUpId: send.programFollowUpId,
          isIntake: enrollment.isIntake,
          activities: send.activities,
        });
      }
    }

    return items.sort((a, b) => {
      const dateCompare = b.scheduledDate.localeCompare(a.scheduledDate);
      if (dateCompare !== 0) return dateCompare;
      return b.defaultSendTime.localeCompare(a.defaultSendTime);
    });
  }, [enrollmentsData?.mailSequenceEnrollmentsForProgram]);

  const hasListItems =
    Boolean(programFollowUps?.length) || upcomingScheduledMails.length > 0;

  // Wait for both queries so we don't flash the empty state before scheduled mail loads.
  const listReady =
    Boolean(programFollowUpsData && !programFollowUpsError) &&
    Boolean(enrollmentsData) &&
    !enrollmentsError;

  // Auto-select the latest sent mail when landing on the tab root (not scheduled).
  useEffect(() => {
    if (!listReady || programFollowUpsLoading) return;
    if (programFollowUpId || scheduledSendId) return;

    if (programFollowUps && programFollowUps.length > 0) {
      navigate(programFollowUps[0].id, { replace: true });
      return;
    }
    if (upcomingScheduledMails.length > 0) {
      navigate(`scheduled/${upcomingScheduledMails[0].id}`, { replace: true });
    }
  }, [
    listReady,
    programFollowUpsLoading,
    programFollowUpId,
    scheduledSendId,
    upcomingScheduledMails,
    programFollowUps,
    navigate,
  ]);

  // If the selected scheduled send disappeared (cancelled/sent), fall back.
  useEffect(() => {
    if (!scheduledSendId || enrollmentsLoading) return;
    if (!enrollmentsData) return;
    if (upcomingScheduledMails.some((mail) => mail.id === scheduledSendId)) {
      return;
    }
    if (programFollowUps && programFollowUps.length > 0) {
      navigate(programFollowUps[0].id, { replace: true });
      return;
    }
    if (upcomingScheduledMails.length > 0) {
      navigate(`scheduled/${upcomingScheduledMails[0].id}`, { replace: true });
    }
  }, [
    scheduledSendId,
    enrollmentsLoading,
    enrollmentsData,
    upcomingScheduledMails,
    programFollowUps,
    navigate,
  ]);

  return (
    <>
      <Transition
        show={listReady}
        {...defaultTransitionProps}
        className="flex h-full w-full flex-grow flex-row items-start border-neutral-75"
      >
        <>
          {!hasListItems ? (
            <EmptyProfileTab
              IconComponent={IllustrationMail}
              iconClassName={'text-secondary-100'}
              titleText={`${patient?.firstName ?? ''}'s mail`}
              subTitleText="Share insights, helpful resources, and activities"
              onClickButton={() => {
                navigate(`/${patient?.programInstanceId}/follow-ups/new`);
              }}
              buttonTitle={`Send mail to ${patient?.firstName}`}
            />
          ) : (
            <>
              <div className="flex h-full w-1/6 min-w-[188px] flex-col items-center justify-start border-r border-neutral-75">
                <div className="flex w-full flex-row items-center justify-center py-4 pr-5">
                  <Button
                    IconComponent={MailIcon}
                    noFocus
                    title="Send Mail"
                    iconClassName="h-[18px] w-[18px]"
                    size="small"
                    className="mt-1 ml-1 w-[194px] justify-between rounded-md pl-5 text-left"
                    onClick={() => {
                      navigate(`/${patient?.programInstanceId}/follow-ups/new`);
                    }}
                  />
                </div>
                <div className="flex h-full w-full flex-col items-center justify-start gap-y-2 overflow-y-auto overscroll-y-contain pb-4 pr-4">
                  {upcomingScheduledMails.map((scheduledMail) => (
                    <FollowUpSelector
                      key={`scheduledMailSelector-${scheduledMail.id}`}
                      id={scheduledMail.id}
                      isIntake={scheduledMail.isIntake}
                      subject={scheduledMail.subject}
                      providerNote={scheduledMail.providerNote}
                      scheduledDate={scheduledMail.scheduledDate}
                      scheduledTime={scheduledMail.defaultSendTime}
                      stepIndex={scheduledMail.stepIndex}
                      sequenceTitle={scheduledMail.sequenceTitle}
                      isScheduled
                      isSelected={scheduledSendId === scheduledMail.id}
                      onClick={() => navigate(`scheduled/${scheduledMail.id}`)}
                    />
                  ))}
                  {programFollowUps?.map((programFollowUp) => (
                    <FollowUpSelector
                      key={`followUpSelector-${programFollowUp.id}`}
                      id={programFollowUp.id}
                      isIntake={programFollowUp.isIntake}
                      subject={programFollowUp.subject}
                      providerNote={programFollowUp.providerNote}
                      createdAt={programFollowUp.createdAt}
                      unreadCommentCount={programFollowUp.unreadCommentCount}
                      isSelected={programFollowUpId === programFollowUp.id}
                      onClick={() => navigate(programFollowUp.id)}
                    />
                  ))}
                </div>
              </div>
              <div className="h-full w-full overflow-hidden">
                <Outlet
                  context={{
                    patient,
                    refetchProgramFollowUps,
                    refetchEnrollments,
                    upcomingScheduledMails,
                    enrollmentsLoading,
                  }}
                />
              </div>
            </>
          )}
        </>
      </Transition>

      {(programFollowUpsLoading || enrollmentsLoading) && !listReady && (
        <Spinner className="mx-auto mt-8" />
      )}
    </>
  );
};

export default FollowUps;
